import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import type { CategoryResponse, CategoryCreate, CategoryUpdate } from "../../api/generated/types.gen";
import { cancelButtonStyle, primaryButtonStyle } from "../../styles/buttonStyles";

// Alias for convenience
type Category = CategoryResponse;

interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CategoryCreate | CategoryUpdate) => Promise<void>;
  category?: Category | null;
  isLoading?: boolean;
  error?: string | null;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  onClose,
  onSubmit,
  category,
  isLoading = false,
  error = null,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when dialog opens/closes or category changes
  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          name: category.name,
          description: category.description || "",
          is_active: category.is_active ?? true,
        });
      } else {
        setFormData({
          name: "",
          description: "",
          is_active: true,
        });
      }
      setErrors({});
    }
  }, [open, category]);

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }

      // No auto-generation needed since we removed slug field
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      // Error handling is done by the parent component
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden", // Prevent scrollbar outside modal
        },
      }}
    >
      <DialogTitle sx={{ px: 4, pt: 4, pb: 2 }}>
        {category ? "Edit Category" : "Create New Category"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent 
          sx={{ 
            px: 4, 
            py: 2,
            overflowY: "auto", // Enable scrolling within content
            overflowX: "hidden", // Prevent horizontal scroll
            maxHeight: "calc(100vh - 200px)", // Limit height to viewport
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleInputChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Enter category name"
              required
              fullWidth
              disabled={isLoading}
              variant="standard"
              sx={{
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleInputChange("description")}
              placeholder="Enter category description"
              fullWidth
              disabled={isLoading}
              variant="standard"
              sx={{
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleInputChange("is_active")}
                  disabled={isLoading}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ gap: 3, px: 4, py: 3 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            disableRipple
            sx={cancelButtonStyle}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            sx={primaryButtonStyle}
          >
            {category ? "Update" : "Create"}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
